export type CaseStudy = {
  client: string;
  sector: string;
  invocations: string;
  latency: string;
  title?: string;
  challenge?: string;
  solution?: string;
  results?: string[];
};

export type Scenario = { title: string; text: string; flow: string[] };

export type ProductDetails = {
  metrics: { value: string; label: string }[];
  useCases: { title: string; text: string }[];
  specs: { title: string; items: string[] }[];
  caseStudies?: CaseStudy[];
  scenarios?: Scenario[];
  architecture?: { ingress: string[]; core: string[]; egress: string[] };
};

const security = {
  title: "Security & Compliance",
  items: ["TLS-encrypted communication", "GDPR-compliant data handling", "Role-based access control", "Full audit trail"],
};

export const productDetails: Record<string, ProductDetails> = {
  firefly: {
    metrics: [
      { value: "<5ms", label: "Processing latency" },
      { value: "99.99%", label: "Uptime" },
      { value: "1B+", label: "Requests per day" },
      { value: "Docker", label: "Native deployment" },
    ],
    useCases: [
      { title: "High-Throughput API Orchestration", text: "Expose and orchestrate low-latency services and APIs that coordinate business flows across dozens of internal and external systems." },
      { title: "Legacy & Core Modernisation", text: "Wrap core and legacy platforms with modern APIs, decoupling channels from back-end systems without disruptive rewrites." },
      { title: "Real-Time Telemetry & IoT", text: "Ingest and route data from devices and sensors in real time, making events instantly available to downstream services." },
    ],
    specs: [
      { title: "Deployment & Runtime", items: ["Docker containers", "On-premises or cloud", "Horizontal scaling", "In-memory processing & caching"] },
      security,
      { title: "Integration", items: ["REST APIs", "Event-driven flows", "Databases & business systems", "Devices & sensors"] },
    ],
    caseStudies: [
      {
        client: "Client 1",
        sector: "Banking",
        invocations: "800,000+",
        latency: "< 36 ms",
        title: "Core banking decoupling & payment processing",
        challenge: "Traffic peaks from digital channels placed heavy load directly on the core banking platform, increasing operating cost and degrading response times during transactional peaks.",
        solution: "FireFly® was deployed as an integration and decoupling layer between digital channels and core systems, handling routing, orchestration, transformation and in-memory caching of payment and account requests before they reach back-end platforms.",
        results: ["800,000+ invocations per day in continuous production", "Average processing time below 36 ms", "Digital channels shielded from core system load"],
      },
      {
        client: "Client 2",
        sector: "E-commerce",
        invocations: "600,000+",
        latency: "< 59 ms",
        title: "High-volume commerce platform integration & real-time event telemetry",
        challenge: "Promotional peaks and seasonal campaigns generated sudden surges of traffic across storefront, order management, inventory and payment systems, which had to stay consistent in real time without degrading response times during peak load.",
        solution: "FireFly® acts as a high-speed API integration gateway between the commerce platform and its back-end services, performing validation, dynamic routing, transformation and real-time emission of telemetry events for continuous monitoring and auditing.",
        results: ["600,000+ invocations per day in mission-critical operation", "Average processing time below 59 ms", "Consistent order and inventory data across systems during traffic peaks"],
      },
    ],
    scenarios: [
      { title: "Core Banking Decoupling", text: "Isolate digital channels from core platforms with an orchestration and caching layer.", flow: ["Digital channels", "FireFly® in-memory", "Core banking"] },
      { title: "Payment Processing", text: "Route, validate and transform high-volume payment requests with low processing times.", flow: ["Payment requests", "FireFly® orchestration", "Payment systems"] },
      { title: "Open Banking Gateway", text: "Expose controlled APIs to third parties with validation and service mediation.", flow: ["Third-party apps", "FireFly® gateway", "Internal services"] },
      { title: "Commerce Platform Integration", text: "Keep storefront, order management, inventory and payment systems consistent in real time during promotional and seasonal traffic peaks.", flow: ["Storefront & channels", "FireFly® gateway", "Commerce back-end"] },
      { title: "Event-Driven Telemetry", text: "Emit order, inventory and transaction events in real time for monitoring, auditing and analytics.", flow: ["Orders & transactions", "FireFly® events", "Monitoring & audit"] },
    ],
    architecture: {
      ingress: ["Digital channels", "External APIs", "Event queues", "Devices & sensors"],
      core: ["Routing & orchestration", "In-memory processing", "Caching", "Transformation"],
      egress: ["Core systems", "Legacy platforms", "Databases", "Business systems"],
    },
  },
  "lemur-monitor": {
    metrics: [
      { value: "Live", label: "Ecosystem visibility" },
      { value: "AI", label: "Anomaly detection" },
      { value: "SLA/OLA", label: "Tracking" },
      { value: "Jira · Teams", label: "Alert integrations" },
    ],
    useCases: [
      { title: "Live SLA/OLA Governance", text: "Track service levels continuously and surface breaches before they impact customers or contractual commitments." },
      { title: "AI-Assisted Root Cause Analysis", text: "Classify errors automatically and correlate them with throughput, status codes and payloads to shorten resolution time." },
      { title: "End-to-End Integration Monitoring", text: "Follow every FireFly® request and back-end dependency in one view, from the first call to the final response." },
    ],
    specs: [
      { title: "Observability", items: ["Time-series metrics", "Payload analysis", "HTTP status & throughput", "Request patterns"] },
      security,
      { title: "Alerting & Integration", items: ["Event-based alerting", "Jira integration", "Microsoft Teams integration", "Configurable thresholds"] },
    ],
    scenarios: [
      { title: "SLA Breach Prevention", text: "Watch service levels continuously and alert the right team before a contractual commitment is missed.", flow: ["Service metrics", "Lemur Monitor® SLA/OLA tracking", "Jira · Teams alerts"] },
      { title: "Incident Root Cause", text: "Classify errors and correlate them with throughput, status codes and payloads to find the cause faster.", flow: ["Errors & payloads", "Lemur Monitor® AI analysis", "Root cause"] },
      { title: "Integration Health", text: "Follow requests end to end through FireFly® and back-end dependencies in a single view.", flow: ["FireFly® requests", "Lemur Monitor® correlation", "End-to-end view"] },
      { title: "Capacity Awareness", text: "Spot changing request patterns early to plan capacity before peaks affect users.", flow: ["Request patterns", "Lemur Monitor® time series", "Capacity insight"] },
    ],
    architecture: {
      ingress: ["FireFly® requests", "Back-end services", "HTTP status & throughput", "Payloads"],
      core: ["Time-series metrics", "AI error classification", "SLA/OLA tracking", "Event-based alerting"],
      egress: ["Live dashboards", "Jira", "Microsoft Teams", "Operations teams"],
    },
  },
  "bee-portfolio": {
    metrics: [
      { value: "Live", label: "Architecture model" },
      { value: "Auto", label: "Service catalogs" },
      { value: "Graph", label: "Dependency mapping" },
      { value: "Cost", label: "Usage insights" },
    ],
    useCases: [
      { title: "Dynamic Service Cataloguing", text: "Keep an always-current catalogue of services, applications, devices and sensors without manual documentation effort." },
      { title: "Change Impact Assessment", text: "Understand which systems, channels and business flows are affected before a change goes to production." },
      { title: "Portfolio Rationalisation", text: "Use real usage and cost data to retire redundant services and focus investment where it creates value." },
    ],
    specs: [
      { title: "Modelling", items: ["Live architecture modelling", "Business flow mapping", "Graph-based dependencies", "Omnichannel views"] },
      security,
      { title: "Data Sources", items: ["FireFly® runtime data", "Service & application inventory", "Usage and cost metrics", "Real-time aggregation"] },
    ],
    scenarios: [
      { title: "Pre-Release Impact Check", text: "See which systems, channels and business flows a change touches before it reaches production.", flow: ["Planned change", "Bee Portfolio® dependency graph", "Impacted flows"] },
      { title: "Always-Current Catalogue", text: "Keep services, applications, devices and sensors catalogued from live data instead of manual documentation.", flow: ["FireFly® runtime data", "Bee Portfolio® cataloguing", "Service catalogue"] },
      { title: "Portfolio Rationalisation", text: "Combine usage and cost data to identify redundant services and guide investment.", flow: ["Usage & cost", "Bee Portfolio® insights", "Investment decisions"] },
      { title: "Architecture Governance", text: "Give architects and management one shared, live view of the landscape for decisions.", flow: ["Live model", "Bee Portfolio® omnichannel views", "Governance board"] },
    ],
    architecture: {
      ingress: ["FireFly® runtime data", "Service inventory", "Applications & devices", "Usage & cost metrics"],
      core: ["Live architecture model", "Graph dependencies", "Business flow mapping", "Real-time aggregation"],
      egress: ["Service catalogues", "Impact analysis", "Cost insights", "Architecture views"],
    },
  },
  "gaia-entity": {
    metrics: [
      { value: "Real-time", label: "Correlation" },
      { value: "Autonomous", label: "Decisions" },
      { value: "Adaptive", label: "AI neurons" },
      { value: "System-wide", label: "Intelligence" },
    ],
    useCases: [
      { title: "Autonomous Operational Decisions", text: "Respond automatically to changing system conditions with AI-driven actions executed continuously and instantly." },
      { title: "Early Anomaly Detection", text: "Detect and classify behavioural deviations across distributed environments before they escalate into incidents." },
      { title: "Cross-Domain Telemetry Correlation", text: "Unify metrics from services and integrations into one coherent view of behaviour and dependencies." },
    ],
    specs: [
      { title: "Intelligence", items: ["Polymorphic AI", "Adaptive neurons", "Behavioural classification", "Continuous learning"] },
      security,
      { title: "Inputs", items: ["Service metrics", "Integration telemetry", "FireFly® & Lemur Monitor® data", "Real-time event streams"] },
    ],
    scenarios: [
      { title: "Autonomous Response", text: "React to changing system conditions with AI-driven actions instead of waiting for manual intervention.", flow: ["System conditions", "Gaia Entity® neurons", "Automated action"] },
      { title: "Early Anomaly Detection", text: "Classify behavioural deviations across distributed environments before they become incidents.", flow: ["Telemetry streams", "Gaia Entity® classification", "Early warning"] },
      { title: "Cross-Domain Correlation", text: "Unify metrics from services and integrations into one coherent picture of behaviour.", flow: ["Service & integration metrics", "Gaia Entity® correlation", "Unified view"] },
      { title: "Operations Intelligence", text: "Enrich FireFly® and Lemur Monitor® data with continuously learned behaviour patterns.", flow: ["FireFly® & Lemur Monitor®", "Gaia Entity® learning", "Decision support"] },
    ],
    architecture: {
      ingress: ["Service metrics", "Integration telemetry", "FireFly® & Lemur Monitor® data", "Event streams"],
      core: ["Polymorphic AI", "Adaptive neurons", "Behavioural classification", "Continuous learning"],
      egress: ["Autonomous actions", "Anomaly alerts", "Correlated insights", "Operations teams"],
    },
  },
  fireant: {
    metrics: [
      { value: "Log-based", label: "Change capture" },
      { value: "Low", label: "Event latency" },
      { value: "Consistent", label: "Synchronisation" },
      { value: "High", label: "Throughput" },
    ],
    useCases: [
      { title: "Real-Time Database Replication", text: "Propagate data changes from source systems as they happen, keeping replicas and downstream services always up to date." },
      { title: "Event-Driven Service Sync", text: "Turn database changes into events that keep distributed services consistent without polling or batch jobs." },
      { title: "Low-Impact Analytics Ingestion", text: "Feed data platforms continuously while minimising load on operational source databases." },
    ],
    specs: [
      { title: "Capture", items: ["Log-based CDC", "Source-level change detection", "Ordered event delivery", "Distributed synchronisation"] },
      security,
      { title: "Delivery", items: ["Real-time events", "Downstream services", "Data platforms", "Integration with FireFly®"] },
    ],
    scenarios: [
      { title: "Database Replication", text: "Propagate changes from source databases as they happen to keep replicas up to date.", flow: ["Source database", "FireAnt® log capture", "Replica"] },
      { title: "Service Synchronisation", text: "Turn data changes into events that keep distributed services consistent without polling.", flow: ["Data changes", "FireAnt® events", "Downstream services"] },
      { title: "Analytics Feed", text: "Stream changes continuously into data platforms while keeping load on operational systems low.", flow: ["Operational DB", "FireAnt® CDC", "Data platform"] },
      { title: "Integration Trigger", text: "Use captured changes to start real-time integration flows through FireFly®.", flow: ["Change event", "FireAnt® delivery", "FireFly® flows"] },
    ],
    architecture: {
      ingress: ["Source databases", "Transaction logs", "Operational systems", "Legacy data stores"],
      core: ["Log-based capture", "Change detection", "Ordered delivery", "Synchronisation"],
      egress: ["Replicas", "Downstream services", "Data platforms", "FireFly® integrations"],
    },
  },
  "koala-flow": {
    metrics: [
      { value: "Single", label: "Delivery view" },
      { value: "Role-based", label: "Team access" },
      { value: "Live", label: "Integration status" },
      { value: "Multi-supplier", label: "Governance" },
    ],
    useCases: [
      { title: "Multi-Supplier Delivery", text: "Coordinate programs, projects and suppliers in one operational view with clear ownership and progress." },
      { title: "Controlled Developer Access", text: "Give developers exactly the services, documentation and resources assigned to their projects, nothing more." },
      { title: "Self-Service Integration Validation", text: "Let developers confirm their FireFly® integrations directly, reducing meetings and manual status reports." },
    ],
    specs: [
      { title: "Management", items: ["Programs & projects", "Suppliers & teams", "Delivery statistics", "Real-time progress"] },
      security,
      { title: "Integration", items: ["FireFly® request visibility", "Service documentation", "Integration resources", "Team leader controls"] },
    ],
    scenarios: [
      { title: "Multi-Supplier Programme", text: "Coordinate several suppliers on one programme with clear ownership and live progress.", flow: ["Suppliers & teams", "Koala Flow® programme view", "Delivery status"] },
      { title: "Supplier Onboarding", text: "Give a new supplier's developers access only to the services and documentation of their project.", flow: ["New supplier", "Koala Flow® role-based access", "Assigned resources"] },
      { title: "Integration Sign-Off", text: "Let developers confirm their FireFly® integrations themselves, reducing meetings and manual reports.", flow: ["Developer tests", "Koala Flow® validation", "Integration confirmed"] },
      { title: "Delivery Reporting", text: "Give management real-time delivery statistics across programmes, projects and suppliers.", flow: ["Project activity", "Koala Flow® statistics", "Management view"] },
    ],
    architecture: {
      ingress: ["Programmes & projects", "Suppliers & teams", "Developers", "FireFly® requests"],
      core: ["Delivery management", "Role-based access", "Integration validation", "Progress tracking"],
      egress: ["Delivery statistics", "Team leaders", "Management", "Supplier reports"],
    },
  },
};
